/**
 * Telling "this quest is not there" apart from "we could not find out".
 *
 * WHY THIS EXISTS — the "Quest not found." bug. `getQuest` used `.single()`,
 * which PostgREST fails with PGRST116 on zero rows, and the adapter threw on
 * any error at all. QuestDetail then read only react-query's `data` and
 * `isPending`, so `!quest` was the union of EVERY failure: quest absent, RLS
 * denial, malformed id, offline, CORS, bad key, retries exhausted. All of them
 * rendered the one string "Quest not found.", which is a plausible-looking
 * message for several causes and the correct one for only some — so a genuine
 * outage is indistinguishable, in the UI and in a bug report, from a quest that
 * was simply cancelled.
 *
 * Note what this does NOT try to do: an RLS denial is deliberately
 * indistinguishable from absence at the data layer, because leaking the
 * existence of a row a caller may not read is the leak RLS exists to prevent.
 * A cancelled quest SHOULD read as "not found" to a logged-out visitor. The
 * distinction being restored here is the one that was never intentional —
 * "absent" versus "broken".
 */
export class QuestNotFoundError extends Error {
  /**
   * A structural marker as well as `instanceof`. Bundle splitting and HMR can
   * produce two copies of a class, and an `instanceof` that silently answers
   * false would land us back on the generic error path — the exact
   * misclassification this module exists to fix.
   */
  readonly isQuestNotFound = true as const;
  readonly questId: string;

  constructor(questId: string) {
    super(`Quest not found: ${questId}`);
    this.name = "QuestNotFoundError";
    this.questId = questId;
  }
}

export function isQuestNotFoundError(err: unknown): err is QuestNotFoundError {
  return (
    err instanceof QuestNotFoundError ||
    (typeof err === "object" && err !== null && (err as { isQuestNotFound?: unknown }).isQuestNotFound === true)
  );
}

/**
 * Why an application was refused, as a stable code rather than Postgres message
 * text (DEV-828).
 *
 * `worker_apply_to_quest` raises human-readable English and puts the code in the
 * exception HINT. The message is a developer artefact — it is not translated and
 * one of them names a table — so the UI localises off `code` (see
 * src/lib/applyErrorMessages.ts) and never renders `message` to a person.
 * `unknown` covers a network failure or any refusal a later migration adds
 * without teaching the client about it.
 */
export type ApplyToQuestErrorCode =
  | "apply_quest_unavailable"
  | "apply_own_quest"
  | "apply_rate_limited"
  | "apply_no_profile"
  | "unknown";

export class ApplyToQuestError extends Error {
  /** Structural marker, for the same reason QuestNotFoundError carries one. */
  readonly isApplyToQuestError = true as const;
  readonly code: ApplyToQuestErrorCode;

  constructor(code: ApplyToQuestErrorCode, message: string) {
    super(message);
    this.name = "ApplyToQuestError";
    this.code = code;
  }
}

export function isApplyToQuestError(err: unknown): err is ApplyToQuestError {
  return (
    err instanceof ApplyToQuestError ||
    (typeof err === "object" &&
      err !== null &&
      (err as { isApplyToQuestError?: unknown }).isApplyToQuestError === true)
  );
}

/**
 * Why minting a referral link was refused, same shape and same reasoning as
 * `ApplyToQuestErrorCode`: `create_quest_referral_link` raises developer-facing
 * English and puts the stable code in the exception HINT, so the message is a
 * developer artefact and only `code` is ever rendered (see
 * src/lib/referralLinkErrorMessages.ts).
 *
 * Two of these the UI must tell apart. `referral_link_revoked` is the only
 * PERMANENT refusal — a revoked link is off until an operator turns it back
 * on, so the generic "please try again" copy would be a lie. `referral_no_profile`
 * is the only one the member can resolve themselves, and the same generic copy
 * sent them round a retry that failed identically forever.
 *
 * Note what is deliberately NOT distinguished. `create_quest_referral_link`
 * raises ONE message for "no such quest" and "quest not open", so a quest id
 * cannot be used to probe for other creators' drafts (spec H4). That coarseness
 * is a security property and is preserved here: both collapse into
 * `referral_quest_unavailable`.
 */
export type ReferralLinkErrorCode =
  | "referral_quest_unavailable"
  | "referral_no_profile"
  | "referral_link_revoked"
  | "unknown";

const REFERRAL_LINK_ERROR_CODES: readonly ReferralLinkErrorCode[] = [
  "referral_quest_unavailable",
  "referral_no_profile",
  "referral_link_revoked",
];

export function isReferralLinkErrorCode(v: unknown): v is ReferralLinkErrorCode {
  return typeof v === "string" && (REFERRAL_LINK_ERROR_CODES as readonly string[]).includes(v);
}

export class ReferralLinkError extends Error {
  /** Structural marker, for the same reason the two errors above carry one. */
  readonly isReferralLinkError = true as const;
  readonly code: ReferralLinkErrorCode;

  constructor(code: ReferralLinkErrorCode, message: string) {
    super(message);
    this.name = "ReferralLinkError";
    this.code = code;
  }
}

export function isReferralLinkError(err: unknown): err is ReferralLinkError {
  return (
    err instanceof ReferralLinkError ||
    (typeof err === "object" &&
      err !== null &&
      (err as { isReferralLinkError?: unknown }).isReferralLinkError === true)
  );
}

/**
 * Why `worker_refer_candidate` (20260923100000) refused a referral, read from
 * the exception HINT.
 *
 * Every code here is about the REFERRER — their own typing, their own link,
 * their own rate — and so is theirs to be told. Nothing about the person being
 * referred ever raises: a suppressed contact, someone already on the quest and
 * the referrer's own contact all return success, so there is deliberately no
 * code for them. The link refusals come from `create_quest_referral_link`,
 * which the RPC calls, so they reuse ReferralLinkErrorCode's values.
 */
export type ReferCandidateErrorCode =
  | Exclude<ReferralLinkErrorCode, "unknown">
  | "referral_name_required"
  | "referral_contact_required"
  | "referral_email_invalid"
  | "referral_phone_invalid"
  | "referral_rate_limited"
  | "unknown";

const REFER_CANDIDATE_ERROR_CODES: readonly ReferCandidateErrorCode[] = [
  ...REFERRAL_LINK_ERROR_CODES.filter((c): c is Exclude<ReferralLinkErrorCode, "unknown"> => c !== "unknown"),
  "referral_name_required",
  "referral_contact_required",
  "referral_email_invalid",
  "referral_phone_invalid",
  "referral_rate_limited",
];

export function isReferCandidateErrorCode(v: unknown): v is ReferCandidateErrorCode {
  return typeof v === "string" && (REFER_CANDIDATE_ERROR_CODES as readonly string[]).includes(v);
}

export class ReferCandidateError extends Error {
  /** Structural marker, for the same reason the errors above carry one. */
  readonly isReferCandidateError = true as const;
  readonly code: ReferCandidateErrorCode;

  constructor(code: ReferCandidateErrorCode, message: string) {
    super(message);
    this.name = "ReferCandidateError";
    this.code = code;
  }
}

export function isReferCandidateError(err: unknown): err is ReferCandidateError {
  return (
    err instanceof ReferCandidateError ||
    (typeof err === "object" &&
      err !== null &&
      (err as { isReferCandidateError?: unknown }).isReferCandidateError === true)
  );
}
